import { toDayjs } from '../format/dates';

import { suggestDates, type BookingDraft, type DraftProblem } from './draft';
import { maxHoldHours } from './stageMoves';

/**
 * The three steps of taking a booking — on the phone and, since 2026-10-22, the website — over the shared booking draft (`@core/bookings/draft`), in the order a
 * customer asks: "do you have this in 40 for the 24th?" — **when, then what, then who**
 * (`docs/Mobile_Redesign_Plan.md`, step 3).
 *
 * <p>
 * The customer is picked last, at checkout, the way a shop's bag works: nobody is asked who they
 * are before they have seen what is free. Three things are required — a day, a garment, a customer
 * — and everything else starts from a default with Change beside it.
 * </p>
 */
export const newBookingSteps = ['when', 'looks', 'bag'] as const;

export type NewBookingStep = (typeof newBookingSteps)[number];

export const stepTitle: Record<NewBookingStep, string> = {
  when: 'When is the event?',
  looks: 'Who wears what',
  bag: 'Bag and confirm',
};

/**
 * Where a problem is fixed on the phone. **Exhaustive over the draft's steps**, so a problem kind
 * added to the shared rules is a compile error here until it has a screen. The customer step splits
 * by its `part`: the dates are the first screen, the payer the last.
 */
export function stepFor(problem: DraftProblem): NewBookingStep {
  switch (problem.step) {
    case 'customer':
      return problem.part === 'payer' ? 'bag' : 'when';
    case 'party':
      return 'looks';
    case 'deposit':
    case 'discount':
    case 'stage':
      return 'bag';
    default: {
      const unhandled: never = problem.step;

      return unhandled;
    }
  }
}

/** The problems a step can fix. */
export function problemsFor(
  step: NewBookingStep,
  problems: readonly DraftProblem[],
): DraftProblem[] {
  return problems.filter((problem) => stepFor(problem) === step);
}

export function nextStep(step: NewBookingStep): NewBookingStep | null {
  return newBookingSteps[newBookingSteps.indexOf(step) + 1] ?? null;
}

export function previousStep(step: NewBookingStep): NewBookingStep | null {
  return newBookingSteps[newBookingSteps.indexOf(step) - 1] ?? null;
}

/** "1 of 3". */
export function stepCount(step: NewBookingStep): string {
  return `${newBookingSteps.indexOf(step) + 1} of ${newBookingSteps.length}`;
}

/** "Wedding on Wednesday 18 November", or "Wedding, Wed 18 – Fri 20 November" over several days. */
export function eventTitle(draft: BookingDraft): string {
  const kind = draft.eventKind.trim() || 'The event';
  const from = toDayjs(draft.chargeFrom);
  const to = toDayjs(draft.chargeTo);

  if (!from || !to || draft.chargeFrom === draft.chargeTo) {
    return `${kind} on ${toDayjs(draft.eventDate)?.format('dddd D MMMM') ?? ''}`;
  }

  return `${kind}, ${from.format('ddd D')} – ${to.format('ddd D MMMM')}`;
}

/** "Sat 24 Oct · out 22 to 26 Oct" — the dates read back above the looks and the bag. */
export function datesLine(draft: BookingDraft): string {
  const event = toDayjs(draft.eventDate);
  const from = toDayjs(draft.possessionFrom);
  const to = toDayjs(draft.possessionTo);

  return event && from && to
    ? `${event.format('ddd D MMM')} · out ${from.format('D')} to ${to.format('D MMM')}`
    : '';
}

/** The hold lengths offered instead of confirming — a day is the default, a week the server's ceiling. */
export const holdChoices = [4, 12, 24, 48, 72, maxHoldHours];

/** "12 hours", "1 day", "7 days". */
export function hoursWord(hours: number): string {
  return hours % 24 === 0 ? `${hours / 24} ${hours === 24 ? 'day' : 'days'}` : `${hours} hours`;
}

/**
 * The event over this many days from its first, the grace either side kept — the "1 day · 2 days ·
 * 3 days" choice beside the month. Nothing until a day is chosen.
 */
export function withEventDays(draft: BookingDraft, days: number): Partial<BookingDraft> {
  const from = toDayjs(draft.eventDate);

  if (!from || days < 1) {
    return {};
  }

  return suggestDates(draft.eventDate, {
    eventTo: from.add(days - 1, 'day').format('YYYY-MM-DD'),
    collectBeforeDays: draft.collectBeforeDays,
    returnAfterDays: draft.returnAfterDays,
  });
}
